/**
 * Pure geometry helpers backing the editable sequence edge (SequenceEdge.tsx).
 *
 * BPMN sequences are orthogonal polylines (right-angle routing): the generated
 * route is always `[start, mid1, mid2, end]` with segments alternating
 * horizontal/vertical (sequence-calculation.util.ts). The endpoints are anchored
 * to the source/target node borders and never move; only interior waypoints are
 * editable, and every edit keeps all segments axis-aligned (the user opted for
 * orthogonal-constrained control points — no diagonals).
 *
 * These are framework-free so they can be unit-tested without React Flow.
 */
import { Position } from '@xyflow/react'

export interface EdgePoint {
  x: number
  y: number
}

/** Coordinates within this tolerance (flow px) count as equal / axis-aligned. */
const EPS = 0.5
/** Tight tolerance for floating-point noise between nodes in one RF group drag. */
const MOVE_EPS = 1e-6
/** Small outward step used when a collapsed short route needs endpoint clearance. */
const ENDPOINT_STUB = 8
/**
 * Distance within which a node drag closes a nearly collinear orthogonal
 * dogleg. Inclusive and aligned with the 10px canvas grid so one-grid offsets
 * merge live instead of leaving a short connector behind.
 */
const NODE_DRAG_MERGE_THRESHOLD = 10
/** Magnetic distance for merging parallel segments during a segment drag. */
const SEGMENT_DRAG_MERGE_THRESHOLD = 5

function samePoint(a: EdgePoint, b: EdgePoint): boolean {
  return Math.abs(a.x - b.x) < EPS && Math.abs(a.y - b.y) < EPS
}

function rectDelta(from: EdgeRect, to: EdgeRect): EdgePoint {
  return { x: to.x - from.x, y: to.y - from.y }
}

export function isHorizontal(a: EdgePoint, b: EdgePoint): boolean {
  return Math.abs(a.y - b.y) < EPS
}

export function isVertical(a: EdgePoint, b: EdgePoint): boolean {
  return Math.abs(a.x - b.x) < EPS
}

/** A segment is axis-aligned when it is horizontal or vertical. */
export function isAxisAligned(a: EdgePoint, b: EdgePoint): boolean {
  return isHorizontal(a, b) || isVertical(a, b)
}

/**
 * Insert deterministic corners wherever a malformed/reconnected route contains
 * a diagonal. The incoming segment's orientation wins, preserving the route's
 * shape while restoring the axis-aligned invariant.
 */
export function repairOrthogonalPoints(points: EdgePoint[]): EdgePoint[] {
  if (points.length < 2) {
    return points
  }
  const repaired: EdgePoint[] = [{ ...points[0] }]
  for (let index = 1; index < points.length; index++) {
    const current = repaired[repaired.length - 1]
    const next = points[index]
    if (!isAxisAligned(current, next)) {
      const incoming = repaired[repaired.length - 2]
      const corner =
        incoming && isVertical(incoming, current)
          ? { x: current.x, y: next.y }
          : { x: next.x, y: current.y }
      repaired.push(corner)
    }
    repaired.push({ ...next })
  }

  return repaired
}

/**
 * Interior waypoint indexes (everything except the two node-anchored endpoints).
 * Returns [] when there are fewer than 3 points.
 */
export function interiorIndexes(points: EdgePoint[]): number[] {
  return points.length < 3 ? [] : points.slice(1, -1).map((_, index) => index + 1)
}

export interface EdgeRect extends EdgePoint {
  width: number
  height: number
}

/**
 * Reconstruct a node's rect from React Flow's live handle position. RF places
 * the default Handle at the CENTER of its declared side, so given the handle
 * coord and the node dimensions we can derive the current top-left — even
 * mid-drag, when the Angular store hasn't received the new position yet. Lets
 * the edge snap against the live node rather than the stale mapper snapshot.
 */
export function rectFromLiveHandle(
  handleX: number,
  handleY: number,
  position: Position,
  dimensions: { width: number; height: number }
): EdgeRect {
  const { width, height } = dimensions
  switch (position) {
    case Position.Left:
      return { x: handleX, y: handleY - height / 2, width, height }
    case Position.Right:
      return { x: handleX - width, y: handleY - height / 2, width, height }
    case Position.Top:
      return { x: handleX - width / 2, y: handleY, width, height }
    default:
      return { x: handleX - width / 2, y: handleY - height, width, height }
  }
}

type RectSide = 'left' | 'right' | 'top' | 'bottom'

interface SegmentRectIntersection {
  point: EdgePoint
  side: RectSide
}

interface RouteRectIntersection extends SegmentRectIntersection {
  segmentIndex: number
}

function between(value: number, a: number, b: number): boolean {
  return value >= Math.min(a, b) - EPS && value <= Math.max(a, b) + EPS
}

function pointOnSideCenter(rect: EdgeRect, side: RectSide): EdgePoint {
  switch (side) {
    case 'left':
      return { x: rect.x, y: rect.y + rect.height / 2 }
    case 'right':
      return { x: rect.x + rect.width, y: rect.y + rect.height / 2 }
    case 'top':
      return { x: rect.x + rect.width / 2, y: rect.y }
    case 'bottom':
      return { x: rect.x + rect.width / 2, y: rect.y + rect.height }
  }
}

function inferRectSide(endpoint: EdgePoint, adjacent: EdgePoint): RectSide {
  const dx = adjacent.x - endpoint.x
  const dy = adjacent.y - endpoint.y

  if (Math.abs(dx) >= Math.abs(dy)) {
    return dx > 0 ? 'right' : 'left'
  }

  return dy > 0 ? 'bottom' : 'top'
}

/**
 * Recover the side from an endpoint already sitting on the rectangle boundary.
 * This handles valid tangent routes: although their first segment does not cross
 * the node body, their persisted endpoint still identifies the attachment side.
 * At a corner, prefer the side whose center is closest to the point.
 */
function sideAtBoundaryPoint(point: EdgePoint, rect: EdgeRect): RectSide | undefined {
  const left = rect.x
  const right = rect.x + rect.width
  const top = rect.y
  const bottom = rect.y + rect.height
  const candidates: RectSide[] = []

  if (Math.abs(point.x - left) < EPS && between(point.y, top, bottom)) {
    candidates.push('left')
  }
  if (Math.abs(point.x - right) < EPS && between(point.y, top, bottom)) {
    candidates.push('right')
  }
  if (Math.abs(point.y - top) < EPS && between(point.x, left, right)) {
    candidates.push('top')
  }
  if (Math.abs(point.y - bottom) < EPS && between(point.x, left, right)) {
    candidates.push('bottom')
  }

  return candidates.reduce<RectSide | undefined>((closest, side) => {
    if (!closest) {
      return side
    }
    const center = pointOnSideCenter(rect, side)
    const closestCenter = pointOnSideCenter(rect, closest)
    const distance = Math.hypot(center.x - point.x, center.y - point.y)
    const closestDistance = Math.hypot(closestCenter.x - point.x, closestCenter.y - point.y)

    return distance < closestDistance ? side : closest
  }, undefined)
}

function oppositeRectSide(side: RectSide): RectSide {
  switch (side) {
    case 'left':
      return 'right'
    case 'right':
      return 'left'
    case 'top':
      return 'bottom'
    case 'bottom':
      return 'top'
  }
}

/**
 * Facing sides on the dominant center-to-center axis. These are the nearest
 * sides of the two connected rectangles; notably, when source.x > target.x the
 * source must face left and the target right (the reverse of RF's fixed hidden
 * source/target handle positions).
 */
function facingRectSides(
  sourceRect: EdgeRect,
  targetRect: EdgeRect
): {
  source: RectSide
  target: RectSide
} {
  const sourceCenter = {
    x: sourceRect.x + sourceRect.width / 2,
    y: sourceRect.y + sourceRect.height / 2,
  }
  const targetCenter = {
    x: targetRect.x + targetRect.width / 2,
    y: targetRect.y + targetRect.height / 2,
  }
  const dx = targetCenter.x - sourceCenter.x
  const dy = targetCenter.y - sourceCenter.y

  if (Math.abs(dx) >= Math.abs(dy)) {
    return dx >= 0 ? { source: 'right', target: 'left' } : { source: 'left', target: 'right' }
  }

  return dy >= 0 ? { source: 'bottom', target: 'top' } : { source: 'top', target: 'bottom' }
}

/** Fallback candidates with the side pointing away from the partner last. */
function fallbackSideOrder(resolvedSide: RectSide, facingSide: RectSide): RectSide[] {
  const awaySide = oppositeRectSide(facingSide)
  const candidates: RectSide[] = [resolvedSide, facingSide, 'left', 'right', 'top', 'bottom']

  return [...new Set(candidates.filter((side) => side !== awaySide)), awaySide]
}

/**
 * Center an endpoint on an explicitly selected rectangle side and move the
 * adjacent waypoint on the shared axis. When no intersection selected a side
 * (legacy/malformed geometry), infer it from the adjacent segment as a fallback.
 */
function snapEndpointCentered(
  endpoint: EdgePoint,
  adjacent: EdgePoint,
  rect: EdgeRect,
  selectedSide?: RectSide
): { endpoint: EdgePoint; adjacent: EdgePoint; orientation: 'h' | 'v' } {
  const side = selectedSide ?? inferRectSide(endpoint, adjacent)
  const centered = pointOnSideCenter(rect, side)

  return side === 'left' || side === 'right'
    ? {
        endpoint: centered,
        adjacent: { x: adjacent.x, y: centered.y },
        orientation: 'h',
      }
    : {
        endpoint: centered,
        adjacent: { x: centered.x, y: adjacent.y },
        orientation: 'v',
      }
}

/**
 * Return the rectangle-boundary intersection nearest `from` on one finite,
 * axis-aligned segment. `from` is always on the route portion that should
 * survive, so choosing its nearest candidate selects the node exit rather than
 * the swallowed entry when a segment crosses the entire node.
 *
 * A segment merely touching a corner or running along a rectangle side does
 * not enter the node body and is therefore not a crossing. This is essential
 * for idempotence: center→intersection adapters intentionally lie on selected
 * node sides and must never consume the route on a later pass.
 */
function segmentIntersectionWithRect(
  from: EdgePoint,
  to: EdgePoint,
  rect: EdgeRect
): SegmentRectIntersection | null {
  if (samePoint(from, to)) {
    return null
  }

  const left = rect.x
  const right = rect.x + rect.width
  const top = rect.y
  const bottom = rect.y + rect.height
  if (isHorizontal(from, to)) {
    // Strict vertical containment excludes top/bottom tangencies. Requiring the
    // segment to continue beyond the boundary excludes endpoint-only touches.
    if (from.y <= top + EPS || from.y >= bottom - EPS) {
      return null
    }
    if (from.x < left - EPS && to.x > left + EPS) {
      return { point: { x: left, y: from.y }, side: 'left' }
    }
    if (from.x > right + EPS && to.x < right - EPS) {
      return { point: { x: right, y: from.y }, side: 'right' }
    }
  } else if (isVertical(from, to)) {
    // Strict horizontal containment excludes left/right tangencies.
    if (from.x <= left + EPS || from.x >= right - EPS) {
      return null
    }
    if (from.y < top - EPS && to.y > top + EPS) {
      return { point: { x: from.x, y: top }, side: 'top' }
    }
    if (from.y > bottom + EPS && to.y < bottom - EPS) {
      return { point: { x: from.x, y: bottom }, side: 'bottom' }
    }
  }

  return null
}

/**
 * Find the first node-boundary crossing while walking from the route that must
 * survive toward an endpoint. For a source this means target→source; for a
 * target it means source→target.
 */
function findRouteRectIntersection(
  points: EdgePoint[],
  rect: EdgeRect,
  endpoint: 'source' | 'target'
): RouteRectIntersection | null {
  if (endpoint === 'source') {
    for (let index = points.length - 2; index >= 0; index--) {
      const hit = segmentIntersectionWithRect(points[index + 1], points[index], rect)
      if (hit) {
        return { ...hit, segmentIndex: index }
      }
    }
  } else {
    for (let index = 0; index < points.length - 1; index++) {
      const hit = segmentIntersectionWithRect(points[index], points[index + 1], rect)
      if (hit) {
        return { ...hit, segmentIndex: index }
      }
    }
  }

  return null
}

function trimSourceToRect(
  points: EdgePoint[],
  rect: EdgeRect
): { points: EdgePoint[]; side?: RectSide } {
  const hit = findRouteRectIntersection(points, rect, 'source')
  if (!hit) {
    return { points }
  }

  return {
    points: [hit.point, ...points.slice(hit.segmentIndex + 1)],
    side: hit.side,
  }
}

function trimTargetToRect(
  points: EdgePoint[],
  rect: EdgeRect
): { points: EdgePoint[]; side?: RectSide } {
  const hit = findRouteRectIntersection(points, rect, 'target')
  if (!hit) {
    return { points }
  }

  return {
    points: [...points.slice(0, hit.segmentIndex + 1), hit.point],
    side: hit.side,
  }
}

function outwardVector(side: RectSide): EdgePoint {
  switch (side) {
    case 'left':
      return { x: -1, y: 0 }
    case 'right':
      return { x: 1, y: 0 }
    case 'top':
      return { x: 0, y: -1 }
    case 'bottom':
      return { x: 0, y: 1 }
  }
}

function steppedRouteBetweenCenters(
  source: EdgePoint,
  sourceSide: RectSide,
  target: EdgePoint,
  targetSide: RectSide
): EdgePoint[] {
  const sourceOutward = outwardVector(sourceSide)
  const targetOutward = outwardVector(targetSide)
  const sourceGap = {
    x: source.x + sourceOutward.x * ENDPOINT_STUB,
    y: source.y + sourceOutward.y * ENDPOINT_STUB,
  }
  const targetGap = {
    x: target.x + targetOutward.x * ENDPOINT_STUB,
    y: target.y + targetOutward.y * ENDPOINT_STUB,
  }
  const sourceTangent = sourceSide === 'left' || sourceSide === 'right' ? 'v' : 'h'
  const targetTangent = targetSide === 'left' || targetSide === 'right' ? 'v' : 'h'

  // Both ends leaving along the same axis means the connector has to cross the
  // corridor between the nodes, and the crossing belongs at its half-point — the
  // shape safeSteppedRouteBetweenCenters tries first. Turning at the target's own
  // stub instead leaves the crossbar ENDPOINT_STUB from the target however wide
  // the corridor is: a long run out of the source and a hard turn against the
  // node it is arriving at.
  if (sourceTangent === targetTangent) {
    const middle =
      sourceTangent === 'v'
        ? [
            { x: (sourceGap.x + targetGap.x) / 2, y: sourceGap.y },
            { x: (sourceGap.x + targetGap.x) / 2, y: targetGap.y },
          ]
        : [
            { x: sourceGap.x, y: (sourceGap.y + targetGap.y) / 2 },
            { x: targetGap.x, y: (sourceGap.y + targetGap.y) / 2 },
          ]

    return normalizeOrthogonalPoints([source, sourceGap, ...middle, targetGap, target])
  }

  // Perpendicular ends meet at a single corner; there is no crossbar to place.
  const corner =
    sourceTangent === 'v' ? { x: targetGap.x, y: sourceGap.y } : { x: sourceGap.x, y: targetGap.y }

  return normalizeOrthogonalPoints([source, sourceGap, corner, targetGap, target])
}

/**
 * Center both endpoints on their topology-selected sides and move each adjacent
 * waypoint on the shared axis. This guarantees that the node-touching source
 * and target segments are perpendicular to their node sides. Short L/Z routes
 * are rebuilt when both endpoints share one interior corner.
 */
function centerTrimmedEndpoints(
  points: EdgePoint[],
  sourceRect: EdgeRect,
  targetRect: EdgeRect,
  sourceSide?: RectSide,
  targetSide?: RectSide
): EdgePoint[] {
  const result = points.map((point) => ({ ...point }))
  const last = result.length - 1
  const original1 = { ...result[1] }
  const resolvedSourceSide = sourceSide ?? inferRectSide(result[0], original1)
  const resolvedTargetSide = targetSide ?? inferRectSide(result[last], result[last - 1])
  const src = snapEndpointCentered(result[0], original1, sourceRect, resolvedSourceSide)
  result[0] = src.endpoint

  if (last >= 3) {
    result[1] = src.adjacent
    const tgt = snapEndpointCentered(result[last], result[last - 1], targetRect, resolvedTargetSide)
    result[last] = tgt.endpoint
    result[last - 1] = tgt.adjacent

    return result
  }

  if (last === 2) {
    const tgt = snapEndpointCentered(result[2], original1, targetRect, resolvedTargetSide)
    result[2] = tgt.endpoint

    if (src.orientation !== tgt.orientation) {
      const horiz = src.orientation === 'h' ? src : tgt
      const vert = src.orientation === 'h' ? tgt : src
      const corner = { x: vert.endpoint.x, y: horiz.endpoint.y }

      return samePoint(corner, src.endpoint) || samePoint(corner, tgt.endpoint)
        ? steppedRouteBetweenCenters(
            src.endpoint,
            resolvedSourceSide,
            tgt.endpoint,
            resolvedTargetSide
          )
        : [result[0], corner, result[2]]
    }

    return src.orientation === 'h'
      ? [
          result[0],
          { x: (src.endpoint.x + tgt.endpoint.x) / 2, y: src.endpoint.y },
          { x: (src.endpoint.x + tgt.endpoint.x) / 2, y: tgt.endpoint.y },
          result[2],
        ]
      : [
          result[0],
          { x: src.endpoint.x, y: (src.endpoint.y + tgt.endpoint.y) / 2 },
          { x: tgt.endpoint.x, y: (src.endpoint.y + tgt.endpoint.y) / 2 },
          result[2],
        ]
  }

  const tgt = snapEndpointCentered(result[1], points[0], targetRect, resolvedTargetSide)
  result[1] = tgt.endpoint
  const directMatchesBothOrientations =
    isAxisAligned(result[0], result[1]) &&
    (src.orientation === 'h') === isHorizontal(result[0], result[1]) &&
    (tgt.orientation === 'h') === isHorizontal(result[0], result[1])

  return directMatchesBothOrientations
    ? result
    : steppedRouteBetweenCenters(src.endpoint, resolvedSourceSide, tgt.endpoint, resolvedTargetSide)
}

/**
 * Reconcile an edited route with its endpoint nodes.
 *
 * For each endpoint, traverse from the route that survives toward its node and
 * trim at the nearest rectangle intersection. This removes loops/waypoints
 * swallowed by the node and, for a through-segment, deliberately chooses the
 * exit nearest the retained route. That intersection selects Left/Right/Top/
 * Bottom; the persisted endpoint is then centered on the selected side and its
 * adjacent waypoint is aligned so the node-touching segment is perpendicular.
 *
 * The operation is iterated to a fixed point because centering one endpoint on
 * a tightly folded legacy route can expose a later crossing at the other.
 */
function snapEndpointsToNodesOnce(
  points: EdgePoint[],
  sourceRect: EdgeRect,
  targetRect: EdgeRect
): EdgePoint[] {
  const source = trimSourceToRect(points, sourceRect)
  const target = trimTargetToRect(source.points, targetRect)
  const sourceSide = source.side ?? sideAtBoundaryPoint(source.points[0], sourceRect)
  const targetSide =
    target.side ?? sideAtBoundaryPoint(target.points[target.points.length - 1], targetRect)

  return normalizeOrthogonalPoints(
    centerTrimmedEndpoints(
      normalizeOrthogonalPoints(target.points),
      sourceRect,
      targetRect,
      sourceSide,
      targetSide
    )
  )
}

function sameRoute(a: EdgePoint[], b: EdgePoint[]): boolean {
  return (
    a.length === b.length &&
    a.every((point, index) => point.x === b[index].x && point.y === b[index].y)
  )
}

function segmentCrossesRectInterior(a: EdgePoint, b: EdgePoint, rect: EdgeRect): boolean {
  const left = rect.x + MOVE_EPS
  const right = rect.x + rect.width - MOVE_EPS
  const top = rect.y + MOVE_EPS
  const bottom = rect.y + rect.height - MOVE_EPS

  if (isHorizontal(a, b)) {
    return a.y > top && a.y < bottom && Math.max(a.x, b.x) > left && Math.min(a.x, b.x) < right
  }
  if (isVertical(a, b)) {
    return a.x > left && a.x < right && Math.max(a.y, b.y) > top && Math.min(a.y, b.y) < bottom
  }

  return true
}

function routeCrossesRectInterior(points: EdgePoint[], rect: EdgeRect): boolean {
  return points.some(
    (point, index) =>
      index < points.length - 1 && segmentCrossesRectInterior(point, points[index + 1], rect)
  )
}

function endpointSegmentsMatchSides(
  points: EdgePoint[],
  sourceSide: RectSide,
  targetSide: RectSide
): boolean {
  const sourcePerpendicular =
    sourceSide === 'left' || sourceSide === 'right'
      ? isHorizontal(points[0], points[1]) &&
        Math.hypot(points[1].x - points[0].x, points[1].y - points[0].y) > EPS
      : isVertical(points[0], points[1]) &&
        Math.hypot(points[1].x - points[0].x, points[1].y - points[0].y) > EPS
  const last = points.length - 1
  const targetPerpendicular =
    targetSide === 'left' || targetSide === 'right'
      ? isHorizontal(points[last - 1], points[last]) &&
        Math.hypot(points[last].x - points[last - 1].x, points[last].y - points[last - 1].y) > EPS
      : isVertical(points[last - 1], points[last]) &&
        Math.hypot(points[last].x - points[last - 1].x, points[last].y - points[last - 1].y) > EPS

  return sourcePerpendicular && targetPerpendicular
}

function routeLength(points: EdgePoint[]): number {
  return points.slice(0, -1).reduce((length, point, index) => {
    const next = points[index + 1]

    return length + Math.abs(next.x - point.x) + Math.abs(next.y - point.y)
  }, 0)
}

/**
 * Route between two centered ports while preserving perpendicular departure and
 * arrival. Candidate Manhattan connectors are tested against both endpoint
 * rectangles; the shortest clean candidate wins.
 */
function safeSteppedRouteBetweenCenters(
  source: EdgePoint,
  sourceSide: RectSide,
  sourceRect: EdgeRect,
  target: EdgePoint,
  targetSide: RectSide,
  targetRect: EdgeRect
): EdgePoint[] | null {
  const sourceOutward = outwardVector(sourceSide)
  const targetOutward = outwardVector(targetSide)
  const sourceTangent = sourceSide === 'left' || sourceSide === 'right' ? 'v' : 'h'
  const targetTangent = targetSide === 'left' || targetSide === 'right' ? 'v' : 'h'
  const leftOuter = Math.min(sourceRect.x, targetRect.x) - ENDPOINT_STUB
  const rightOuter =
    Math.max(sourceRect.x + sourceRect.width, targetRect.x + targetRect.width) + ENDPOINT_STUB
  const topOuter = Math.min(sourceRect.y, targetRect.y) - ENDPOINT_STUB
  const bottomOuter =
    Math.max(sourceRect.y + sourceRect.height, targetRect.y + targetRect.height) + ENDPOINT_STUB

  for (const stub of [ENDPOINT_STUB, 4, 2, 1]) {
    const sourceGap = {
      x: source.x + sourceOutward.x * stub,
      y: source.y + sourceOutward.y * stub,
    }
    const targetGap = {
      x: target.x + targetOutward.x * stub,
      y: target.y + targetOutward.y * stub,
    }
    const connectors: EdgePoint[][] = []

    if (sourceTangent === 'h' && targetTangent === 'h') {
      for (const splitX of [(sourceGap.x + targetGap.x) / 2, leftOuter, rightOuter]) {
        connectors.push([
          sourceGap,
          { x: splitX, y: sourceGap.y },
          { x: splitX, y: targetGap.y },
          targetGap,
        ])
      }
    } else if (sourceTangent === 'v' && targetTangent === 'v') {
      for (const splitY of [(sourceGap.y + targetGap.y) / 2, topOuter, bottomOuter]) {
        connectors.push([
          sourceGap,
          { x: sourceGap.x, y: splitY },
          { x: targetGap.x, y: splitY },
          targetGap,
        ])
      }
    } else {
      // Perpendicular sides admit two L connectors, and they are the same length:
      // turn at the source's coordinate, or turn at the target's. Only offering
      // one of them left routes bending hard against the node they arrive at,
      // whichever way round the pair happened to be. The one that turns early
      // collapses to a bare three-point L; the other has to keep both clearance
      // stubs to leave and arrive perpendicular, and the tie-break below drops it.
      connectors.push([sourceGap, { x: sourceGap.x, y: targetGap.y }, targetGap])
      connectors.push([sourceGap, { x: targetGap.x, y: sourceGap.y }, targetGap])
    }

    // Generic outer-box detours complete the candidate set when a simple L/Z
    // corridor is blocked. The invariant filter below keeps only variants whose
    // first and last connector turns match the selected side tangents.
    for (const splitX of [leftOuter, rightOuter]) {
      for (const splitY of [topOuter, bottomOuter]) {
        connectors.push([
          sourceGap,
          { x: splitX, y: sourceGap.y },
          { x: splitX, y: splitY },
          { x: targetGap.x, y: splitY },
          targetGap,
        ])
        connectors.push([
          sourceGap,
          { x: sourceGap.x, y: splitY },
          { x: splitX, y: splitY },
          { x: splitX, y: targetGap.y },
          targetGap,
        ])
      }
    }

    const valid = connectors
      .map((connector) => normalizeOrthogonalPoints([source, ...connector, target]))
      .filter(
        (candidate) =>
          isOrthogonalRoute(candidate) &&
          endpointSegmentsMatchSides(candidate, sourceSide, targetSide) &&
          !routeCrossesRectInterior(candidate, sourceRect) &&
          !routeCrossesRectInterior(candidate, targetRect)
      )
      // Equal-length candidates are common — every monotone L between the same
      // two ports measures the same. Prefer the one that bends least.
      .sort((a, b) => routeLength(a) - routeLength(b) || a.length - b.length)

    if (valid[0]) {
      return valid[0]
    }
  }

  return null
}

function positionToRectSide(position: Position): RectSide {
  switch (position) {
    case Position.Left:
      return 'left'
    case Position.Right:
      return 'right'
    case Position.Top:
      return 'top'
    case Position.Bottom:
      return 'bottom'
  }
}

/**
 * Build a clean initial route between explicitly selected node-border ports.
 * Unlike the lightweight preview polyline, this keeps arbitrary side choices
 * outside both endpoint rectangles (for example, entering a gateway from its
 * far side instead of cutting through the diamond).
 */
export function routeBetweenRectPositions(
  sourceRect: EdgeRect,
  sourcePosition: Position,
  targetRect: EdgeRect,
  targetPosition: Position
): EdgePoint[] {
  const sourceSide = positionToRectSide(sourcePosition)
  const targetSide = positionToRectSide(targetPosition)
  const source = pointOnSideCenter(sourceRect, sourceSide)
  const target = pointOnSideCenter(targetRect, targetSide)

  return (
    safeSteppedRouteBetweenCenters(
      source,
      sourceSide,
      sourceRect,
      target,
      targetSide,
      targetRect
    ) ?? computeStepPolyline(source, sourcePosition, target, targetPosition)
  )
}

function ensureEndpointInvariant(
  points: EdgePoint[],
  sourceRect: EdgeRect,
  targetRect: EdgeRect
): EdgePoint[] {
  const sourceSide = sideAtBoundaryPoint(points[0], sourceRect)
  const targetSide = sideAtBoundaryPoint(points[points.length - 1], targetRect)
  if (
    sourceSide &&
    targetSide &&
    isOrthogonalRoute(points) &&
    endpointSegmentsMatchSides(points, sourceSide, targetSide) &&
    !routeCrossesRectInterior(points, sourceRect) &&
    !routeCrossesRectInterior(points, targetRect)
  ) {
    return points
  }

  const resolvedSourceSide = sourceSide ?? inferRectSide(points[0], points[1])
  const last = points.length - 1
  const resolvedTargetSide = targetSide ?? inferRectSide(points[last], points[last - 1])

  const preferred = safeSteppedRouteBetweenCenters(
    pointOnSideCenter(sourceRect, resolvedSourceSide),
    resolvedSourceSide,
    sourceRect,
    pointOnSideCenter(targetRect, resolvedTargetSide),
    resolvedTargetSide,
    targetRect
  )
  if (preferred) {
    return preferred
  }

  const facingSides = facingRectSides(sourceRect, targetRect)
  const sourceSideOrder = fallbackSideOrder(resolvedSourceSide, facingSides.source)
  const targetSideOrder = fallbackSideOrder(resolvedTargetSide, facingSides.target)
  for (const alternateSourceSide of sourceSideOrder) {
    for (const alternateTargetSide of targetSideOrder) {
      const alternate = safeSteppedRouteBetweenCenters(
        pointOnSideCenter(sourceRect, alternateSourceSide),
        alternateSourceSide,
        sourceRect,
        pointOnSideCenter(targetRect, alternateTargetSide),
        alternateTargetSide,
        targetRect
      )
      if (alternate) {
        return alternate
      }
    }
  }

  // Only overlapping/touching rectangles should have no clean corridor. Node
  // placement validation prevents this fallback from being persisted. Keep
  // even that invalid transient frame local rather than routing behind nodes.
  return steppedRouteBetweenCenters(
    pointOnSideCenter(sourceRect, facingSides.source),
    facingSides.source,
    pointOnSideCenter(targetRect, facingSides.target),
    facingSides.target
  )
}

export function snapEndpointsToNodes(
  points: EdgePoint[],
  sourceRect: EdgeRect,
  targetRect: EdgeRect
): EdgePoint[] {
  if (points.length < 2) {
    return points
  }

  let result = points
  for (let pass = 0; pass < 6; pass++) {
    const next = snapEndpointsToNodesOnce(result, sourceRect, targetRect)
    if (sameRoute(result, next)) {
      return ensureEndpointInvariant(next, sourceRect, targetRect)
    }
    result = next
  }

  return ensureEndpointInvariant(result, sourceRect, targetRect)
}

/**
 * Canonicalize a route for a live preview.
 *
 * Where the route runs past an endpoint node and doubles back, trim it at the
 * border it first meets and end it there — keeping the coordinate the pointer
 * chose, which is the whole difference from snapEndpointsToNodes. That centres
 * the endpoint on its side and, failing that, rebuilds the route between side
 * centres: correct for geometry about to be persisted, wrong under a pointer,
 * because it discards what the user is dragging and, being derived from the two
 * rects alone, hands back the same route on every frame while the pointer moves.
 *
 * Some drags have no clean route to offer — a segment spanning a node's full
 * width has to cross it to get past it. There the trimmed route still crosses,
 * and the honest preview is the one the pointer asked for, so the input stands.
 */
export function previewRoute(
  points: EdgePoint[],
  sourceRect: EdgeRect,
  targetRect: EdgeRect
): EdgePoint[] {
  if (points.length < 2) {
    return points
  }
  const trimmed = normalizeOrthogonalPoints(
    repairOrthogonalPoints(
      trimTargetToRect(trimSourceToRect(points, sourceRect).points, targetRect).points
    )
  )
  if (!isOrthogonalRoute(trimmed) || trimmed.length < 2) {
    return points
  }
  const crossedBefore =
    routeCrossesRectInterior(points, sourceRect) || routeCrossesRectInterior(points, targetRect)
  const crossesAfter =
    routeCrossesRectInterior(trimmed, sourceRect) || routeCrossesRectInterior(trimmed, targetRect)

  return crossedBefore && crossesAfter ? points : trimmed
}

/**
 * Auto-routed step polyline between two node-attached endpoints — the fallback
 * shape drawn (and editable) when a sequence has no valid stored waypoints yet.
 * Endpoints come from React Flow's node-border attach math (sourceX/Y with a
 * Position enum), so the polyline is always geometrically consistent with the
 * current node positions.
 *
 * Shape:
 *  - Aligned along the primary axis (source and target face each other on the
 *    same row/column) → a single straight segment.
 *  - Same axis, offset perpendicular → a Z with the midpoint at the average of
 *    the two endpoints on that axis.
 *  - Mixed axes (e.g. right → top) → an L: turn once at the corner that keeps
 *    each endpoint stepping OUT in its handle direction.
 *
 * We match the aesthetic of the React Flow editable-edge "Step" example: sharp
 * right-angle corners, no gap offsets — the endpoints are already on the node
 * border, so stepping out isn't necessary. Once the user drags any segment /
 * corner, the resulting polyline is persisted as the sequence's layoutOptions.
 */
export function computeStepPolyline(
  source: EdgePoint,
  sourcePosition: Position,
  target: EdgePoint,
  targetPosition: Position
): EdgePoint[] {
  const sourceHorizontal = sourcePosition === Position.Left || sourcePosition === Position.Right
  const targetHorizontal = targetPosition === Position.Left || targetPosition === Position.Right

  // Straight shot: both handles face along the same axis and already line up.
  if (sourceHorizontal && targetHorizontal && Math.abs(source.y - target.y) < EPS) {
    return [source, target]
  }
  if (!sourceHorizontal && !targetHorizontal && Math.abs(source.x - target.x) < EPS) {
    return [source, target]
  }

  // Both horizontal-facing (right ↔ left, most BPMN sequences): Z with a
  // vertical middle segment at the average x of the two endpoints.
  if (sourceHorizontal && targetHorizontal) {
    const midX = (source.x + target.x) / 2
    return [source, { x: midX, y: source.y }, { x: midX, y: target.y }, target]
  }

  // Both vertical-facing: mirror the above with a horizontal middle at avg y.
  if (!sourceHorizontal && !targetHorizontal) {
    const midY = (source.y + target.y) / 2
    return [source, { x: source.x, y: midY }, { x: target.x, y: midY }, target]
  }

  // Mixed: one L-turn. The corner sits on the source's step-out axis so the
  // handle exits its node the direction it's facing.
  const corner: EdgePoint = sourceHorizontal
    ? { x: target.x, y: source.y }
    : { x: source.x, y: target.y }
  return [source, corner, target]
}

/**
 * Move interior vertex `index` toward (x, y) while keeping the two adjacent
 * segments axis-aligned:
 *  - an interior neighbor follows on the segment's shared axis (so a corner
 *    drags freely in 2D, and a mid-segment point translates its whole segment);
 *  - a neighbor that is a fixed endpoint constrains the vertex on the axis that
 *    would otherwise move that endpoint (so the node anchor stays put).
 *
 * Returns a new array; endpoints (index 0 and last) are returned unchanged.
 */
export function moveVertexOrthogonal(
  points: EdgePoint[],
  index: number,
  x: number,
  y: number
): EdgePoint[] {
  const last = points.length - 1
  if (index <= 0 || index >= last) {
    return points
  }

  const prev = index - 1
  const next = index + 1
  const prevIsEndpoint = prev === 0
  const nextIsEndpoint = next === last
  // Orientation of each adjacent segment BEFORE the move.
  const prevHorizontal = isHorizontal(points[prev], points[index])
  const nextHorizontal = isHorizontal(points[index], points[next])

  let vx = x
  let vy = y
  // Endpoint neighbors are immovable — pin the vertex on the axis that keeps
  // their shared segment aligned.
  if (prevIsEndpoint) {
    if (prevHorizontal) {
      vy = points[prev].y
    } else {
      vx = points[prev].x
    }
  }
  if (nextIsEndpoint) {
    if (nextHorizontal) {
      vy = points[next].y
    } else {
      vx = points[next].x
    }
  }

  const result = points.map((point) => ({ ...point }))
  result[index] = { x: vx, y: vy }
  // Interior neighbors follow so their shared segment stays axis-aligned.
  if (!prevIsEndpoint) {
    result[prev] = prevHorizontal ? { ...result[prev], y: vy } : { ...result[prev], x: vx }
  }
  if (!nextIsEndpoint) {
    result[next] = nextHorizontal ? { ...result[next], y: vy } : { ...result[next], x: vx }
  }

  return result
}

/**
 * Move the whole segment `segmentIndex → segmentIndex + 1` perpendicular to its
 * own orientation by `perpDelta` (flow px): a horizontal segment shifts in y, a
 * vertical one in x. When an end of the segment is a fixed node-anchored
 * endpoint (index 0 or the last index) a right-angle riser is inserted so the
 * endpoint stays put and the path stays orthogonal; an interior corner just
 * translates. This is what lets a straight edge (a single segment between two
 * anchored endpoints) gain a bend.
 *
 * Returns a new array; an out-of-range segment index returns the input unchanged.
 */
export function moveSegmentOrthogonal(
  points: EdgePoint[],
  segmentIndex: number,
  perpDelta: number
): EdgePoint[] {
  const last = points.length - 1
  if (segmentIndex < 0 || segmentIndex >= last) {
    return points
  }
  const start = points[segmentIndex]
  const end = points[segmentIndex + 1]
  const shift = isHorizontal(start, end) ? { x: 0, y: perpDelta } : { x: perpDelta, y: 0 }
  const newStart = { x: start.x + shift.x, y: start.y + shift.y }
  const newEnd = { x: end.x + shift.x, y: end.y + shift.y }
  // Keep a fixed endpoint in place and add a riser corner; move an interior corner.
  const startPart = segmentIndex === 0 ? [start, newStart] : [newStart]
  const endPart = segmentIndex + 1 === last ? [newEnd, end] : [newEnd]

  return [
    ...points.slice(0, segmentIndex),
    ...startPart,
    ...endPart,
    ...points.slice(segmentIndex + 2),
  ]
}

function perpendicularSideForSegment(
  endpoint: EdgePoint,
  adjacent: EdgePoint,
  rect: EdgeRect,
  horizontal: boolean
): RectSide {
  const boundarySide = sideAtBoundaryPoint(endpoint, rect)
  if (
    boundarySide &&
    (horizontal
      ? boundarySide === 'left' || boundarySide === 'right'
      : boundarySide === 'top' || boundarySide === 'bottom')
  ) {
    return boundarySide
  }

  return inferRectSide(endpoint, adjacent)
}

/**
 * Reconcile an endpoint leg the drag lengthened rather than moved.
 *
 * Shifting a segment also changes the length of the leg beside it, and that leg
 * can end up pointing back into the node it is attached to: the endpoint stays
 * glued to a side the route no longer leaves from. Two ways out, both keeping the
 * dragged line exactly where the pointer put it — when the far end stopped inside
 * the node's span no leg along this axis can reach it, so the endpoint moves onto
 * the perpendicular side the route continues towards and the leg collapses; when
 * the leg runs clean through, the endpoint moves to the opposite side and the leg
 * survives.
 *
 * moveSegmentForDrag reconciles only the endpoint the dragged segment owns, which
 * is why this exists: the leg at the other end changes too, and used to stay
 * inside its node for the whole gesture until the drop rebuilt the route.
 */
function reattachEndpointLeg(
  points: EdgePoint[],
  end: 'source' | 'target',
  rect: EdgeRect
): EdgePoint[] {
  const last = points.length - 1
  if (last < 2) {
    return points
  }
  const endpoint = points[end === 'source' ? 0 : last]
  const adjacent = points[end === 'source' ? 1 : last - 1]
  const beyond = points[end === 'source' ? 2 : last - 2]
  if (samePoint(endpoint, adjacent) || !isAxisAligned(endpoint, adjacent)) {
    return points
  }

  const vertical = isVertical(endpoint, adjacent)
  const lo = vertical ? rect.y : rect.x
  const hi = vertical ? rect.y + rect.height : rect.x + rect.width
  const near = vertical ? endpoint.y : endpoint.x
  const far = vertical ? adjacent.y : adjacent.x

  // Already fine: the leg leaves the node on the side its endpoint sits on.
  if ((near <= lo + EPS && far < near) || (near >= hi - EPS && far > near)) {
    return points
  }

  if (far > lo + EPS && far < hi - EPS) {
    const crossNear = vertical ? endpoint.x : endpoint.y
    const crossBeyond = vertical ? beyond.x : beyond.y
    const towardsHigh = crossBeyond > crossNear
    const cross = vertical
      ? towardsHigh
        ? rect.x + rect.width
        : rect.x
      : towardsHigh
        ? rect.y + rect.height
        : rect.y
    const moved = vertical ? { x: cross, y: far } : { x: far, y: cross }

    return end === 'source' ? [moved, ...points.slice(2)] : [...points.slice(0, last - 1), moved]
  }

  const moved = vertical
    ? { x: endpoint.x, y: far < near ? lo : hi }
    : { x: far < near ? lo : hi, y: endpoint.y }

  return end === 'source' ? [moved, ...points.slice(1)] : [...points.slice(0, last), moved]
}

/**
 * Build the transient preview for a segment drag.
 *
 * Endpoint segments differ from persisted geometry while the pointer is down:
 * the endpoint follows the segment along its current node side so the user can
 * freely slide it. If the shifted line leaves that side, a new perpendicular
 * leg is inserted from the center of the nearest adjacent side to the dragged
 * line. Drop canonicalization later recenters an endpoint that remained on its
 * original side, or persists the newly created leg after a side transition.
 */
export function moveSegmentForDrag(
  points: EdgePoint[],
  segmentIndex: number,
  perpDelta: number,
  sourceRect: EdgeRect,
  targetRect: EdgeRect
): EdgePoint[] {
  const last = points.length - 1
  if (segmentIndex < 0 || segmentIndex >= last) {
    return points
  }
  // Whether the drag lengthens an endpoint leg instead of moving it: the shift
  // carries the far end of the neighbouring leg with it. Read off the incoming
  // route, because the endpoint handling below can insert points.
  const lengthensSourceLeg = segmentIndex === 1
  const lengthensTargetLeg = segmentIndex === last - 2

  if (segmentIndex > 0 && segmentIndex + 1 < last) {
    const interior = moveSegmentOrthogonal(points, segmentIndex, perpDelta)
    if (!lengthensSourceLeg && !lengthensTargetLeg) {
      return interior
    }
    const withSource = lengthensSourceLeg
      ? reattachEndpointLeg(interior, 'source', sourceRect)
      : interior

    return normalizeOrthogonalPoints(
      lengthensTargetLeg ? reattachEndpointLeg(withSource, 'target', targetRect) : withSource
    )
  }

  const start = points[segmentIndex]
  const end = points[segmentIndex + 1]
  const horizontal = isHorizontal(start, end)
  const shift = horizontal ? { x: 0, y: perpDelta } : { x: perpDelta, y: 0 }
  let result = points.map((point) => ({ ...point }))
  result[segmentIndex] = { x: start.x + shift.x, y: start.y + shift.y }
  result[segmentIndex + 1] = { x: end.x + shift.x, y: end.y + shift.y }

  if (segmentIndex === 0) {
    const side = perpendicularSideForSegment(points[0], points[1], sourceRect, horizontal)
    if (horizontal && between(result[0].y, sourceRect.y, sourceRect.y + sourceRect.height)) {
      result[0] = {
        x: side === 'left' ? sourceRect.x : sourceRect.x + sourceRect.width,
        y: result[0].y,
      }
    } else if (!horizontal && between(result[0].x, sourceRect.x, sourceRect.x + sourceRect.width)) {
      result[0] = {
        x: result[0].x,
        y: side === 'top' ? sourceRect.y : sourceRect.y + sourceRect.height,
      }
    } else {
      const closestSide: RectSide = horizontal
        ? result[0].y < sourceRect.y
          ? 'top'
          : 'bottom'
        : result[0].x < sourceRect.x
          ? 'left'
          : 'right'
      const centered = pointOnSideCenter(sourceRect, closestSide)
      const connector = horizontal
        ? { x: centered.x, y: result[0].y }
        : { x: result[0].x, y: centered.y }
      result = [centered, connector, ...result.slice(1)]
    }
  }

  if (segmentIndex + 1 === last) {
    const originalEndpoint = points[last]
    const originalAdjacent = points[last - 1]
    const side = perpendicularSideForSegment(
      originalEndpoint,
      originalAdjacent,
      targetRect,
      horizontal
    )
    const targetIndex = result.length - 1
    if (
      horizontal &&
      between(result[targetIndex].y, targetRect.y, targetRect.y + targetRect.height)
    ) {
      result[targetIndex] = {
        x: side === 'left' ? targetRect.x : targetRect.x + targetRect.width,
        y: result[targetIndex].y,
      }
    } else if (
      !horizontal &&
      between(result[targetIndex].x, targetRect.x, targetRect.x + targetRect.width)
    ) {
      result[targetIndex] = {
        x: result[targetIndex].x,
        y: side === 'top' ? targetRect.y : targetRect.y + targetRect.height,
      }
    } else {
      const closestSide: RectSide = horizontal
        ? result[targetIndex].y < targetRect.y
          ? 'top'
          : 'bottom'
        : result[targetIndex].x < targetRect.x
          ? 'left'
          : 'right'
      const centered = pointOnSideCenter(targetRect, closestSide)
      const connector = horizontal
        ? { x: centered.x, y: result[targetIndex].y }
        : { x: result[targetIndex].x, y: centered.y }
      result = [...result.slice(0, -1), connector, centered]
    }
  }

  if (lengthensSourceLeg) {
    result = reattachEndpointLeg(result, 'source', sourceRect)
  }
  if (lengthensTargetLeg) {
    result = reattachEndpointLeg(result, 'target', targetRect)
  }

  return normalizeOrthogonalPoints(result)
}

export interface SegmentDragUpdate {
  points: EdgePoint[]
  /** Index of the segment that remains under the pointer after topology changes. */
  segmentIndex: number
  /** True when this frame removed one or more connector segments. */
  merged: boolean
}

function segmentAxis(points: EdgePoint[], segmentIndex: number, horizontal: boolean): number {
  return horizontal ? points[segmentIndex].y : points[segmentIndex].x
}

function segmentParallelMidpoint(
  points: EdgePoint[],
  segmentIndex: number,
  horizontal: boolean
): number {
  const start = points[segmentIndex]
  const end = points[segmentIndex + 1]

  return horizontal ? (start.x + end.x) / 2 : (start.y + end.y) / 2
}

/** Find the segment that best represents the line currently held by the pointer. */
function closestParallelSegment(
  points: EdgePoint[],
  horizontal: boolean,
  axis: number,
  parallelPosition: number
): number {
  let bestIndex = 0
  let bestScore = Number.POSITIVE_INFINITY

  for (let index = 0; index < points.length - 1; index++) {
    const start = points[index]
    const end = points[index + 1]
    if (horizontal === isHorizontal(start, end)) {
      const candidateAxis = horizontal ? start.y : start.x
      const candidateStart = horizontal ? start.x : start.y
      const candidateEnd = horizontal ? end.x : end.y
      const intervalDistance =
        parallelPosition < Math.min(candidateStart, candidateEnd)
          ? Math.min(candidateStart, candidateEnd) - parallelPosition
          : parallelPosition > Math.max(candidateStart, candidateEnd)
            ? parallelPosition - Math.max(candidateStart, candidateEnd)
            : 0
      const score = Math.abs(candidateAxis - axis) * 1_000_000 + intervalDistance
      if (score < bestScore) {
        bestIndex = index
        bestScore = score
      }
    }
  }

  return bestIndex
}

interface ParallelMergeCandidate {
  otherSegmentIndex: number
  connectorLength: number
}

function nearbyParallelCandidates(
  points: EdgePoint[],
  segmentIndex: number,
  horizontal: boolean
): ParallelMergeCandidate[] {
  const lastSegment = points.length - 2

  return [segmentIndex - 2, segmentIndex + 2]
    .filter((candidate) => candidate >= 0 && candidate <= lastSegment)
    .filter((candidate) => horizontal === isHorizontal(points[candidate], points[candidate + 1]))
    .map((otherSegmentIndex) => {
      const connectorIndex = Math.min(segmentIndex, otherSegmentIndex) + 1
      const connectorStart = points[connectorIndex]
      const connectorEnd = points[connectorIndex + 1]

      return {
        otherSegmentIndex,
        connectorLength:
          Math.abs(connectorEnd.x - connectorStart.x) + Math.abs(connectorEnd.y - connectorStart.y),
      }
    })
    .filter(({ connectorLength }) => connectorLength <= SEGMENT_DRAG_MERGE_THRESHOLD)
    .sort((a, b) => a.connectorLength - b.connectorLength)
}

/**
 * Merge the active segment with an immediately neighboring parallel segment.
 *
 * The dragged line owns the merged axis, so it stays under the pointer instead
 * of jumping to an average or endpoint-centered position. Endpoint centering is
 * deliberately deferred until drop while the active gesture owns the preview.
 * A route spanning both centered endpoints can collapse only when those endpoint
 * axes already match; otherwise a straight segment could not preserve both.
 */
function mergeNearbyParallelSegmentOnce(
  points: EdgePoint[],
  segmentIndex: number,
  horizontal: boolean,
  draggedAxis: number
): SegmentDragUpdate {
  const lastSegment = points.length - 2

  const candidate = nearbyParallelCandidates(points, segmentIndex, horizontal).find(
    ({ otherSegmentIndex }) => {
      const firstSegment = Math.min(segmentIndex, otherSegmentIndex)
      const secondSegment = Math.max(segmentIndex, otherSegmentIndex)
      const activeAxis = segmentAxis(points, segmentIndex, horizontal)
      const otherAxis = segmentAxis(points, otherSegmentIndex, horizontal)
      const spansBothEndpoints = firstSegment === 0 && secondSegment === lastSegment
      const mergedStart = points[firstSegment]
      const mergedEnd = points[secondSegment + 1]
      const parallelStart = horizontal ? mergedStart.x : mergedStart.y
      const parallelEnd = horizontal ? mergedEnd.x : mergedEnd.y

      return (
        (!spansBothEndpoints || Math.abs(activeAxis - otherAxis) <= MOVE_EPS) &&
        // A zero-length merged segment would disappear under the pointer and
        // make continuation ambiguous. Keep the tiny loop editable instead.
        Math.abs(parallelEnd - parallelStart) > MOVE_EPS
      )
    }
  )
  if (!candidate) {
    return { points, segmentIndex, merged: false }
  }

  const { otherSegmentIndex } = candidate
  const firstSegment = Math.min(segmentIndex, otherSegmentIndex)
  const secondSegment = Math.max(segmentIndex, otherSegmentIndex)
  const mergedStart = points[firstSegment]
  const mergedEnd = points[secondSegment + 1]
  const parallelStart = horizontal ? mergedStart.x : mergedStart.y
  const parallelEnd = horizontal ? mergedEnd.x : mergedEnd.y
  const start = horizontal
    ? { x: mergedStart.x, y: draggedAxis }
    : { x: draggedAxis, y: mergedStart.y }
  const end = horizontal ? { x: mergedEnd.x, y: draggedAxis } : { x: draggedAxis, y: mergedEnd.y }
  const merged = normalizeOrthogonalPoints([
    ...points.slice(0, firstSegment),
    start,
    end,
    ...points.slice(secondSegment + 2),
  ])
  const parallelPosition = (parallelStart + parallelEnd) / 2
  const nextSegmentIndex = closestParallelSegment(merged, horizontal, draggedAxis, parallelPosition)

  return { points: merged, segmentIndex: nextSegmentIndex, merged: true }
}

/**
 * Produce one live segment-drag frame and magnetically collapse any connector
 * that reaches the merge threshold. The returned segment index tracks the same
 * physical line through point-list changes, allowing the caller to rebase the
 * gesture and continue dragging the merged segment without interruption.
 */
export function moveSegmentAndMergeForDrag(
  points: EdgePoint[],
  segmentIndex: number,
  perpDelta: number,
  sourceRect: EdgeRect,
  targetRect: EdgeRect
): SegmentDragUpdate {
  if (segmentIndex < 0 || segmentIndex >= points.length - 1) {
    return { points, segmentIndex, merged: false }
  }

  const horizontal = isHorizontal(points[segmentIndex], points[segmentIndex + 1])
  const intendedAxis = segmentAxis(points, segmentIndex, horizontal) + perpDelta
  const parallelPosition = segmentParallelMidpoint(points, segmentIndex, horizontal)
  const endpointSegment = segmentIndex === 0 || segmentIndex + 1 === points.length - 1
  const moved = moveSegmentForDrag(points, segmentIndex, perpDelta, sourceRect, targetRect)
  // Detect and apply proximity merges before endpoint reconciliation. This keeps
  // the active line on the pointer axis even when the merged route now reaches
  // a node; the normal drop path will center that endpoint exactly once.
  let result = normalizeOrthogonalPoints(repairOrthogonalPoints(moved))
  let activeSegmentIndex = closestParallelSegment(
    result,
    horizontal,
    intendedAxis,
    parallelPosition
  )
  let merged = result.length < moved.length || result.length < points.length

  // A collapse can expose a second tiny connector on the other side. Continue
  // to a fixed point while retaining the segment that is still under the pointer.
  for (let pass = 0; pass < points.length; pass++) {
    const next = mergeNearbyParallelSegmentOnce(
      result,
      activeSegmentIndex,
      horizontal,
      intendedAxis
    )
    if (!next.merged) {
      break
    }
    result = next.points
    activeSegmentIndex = next.segmentIndex
    merged = true
  }

  if (merged || endpointSegment) {
    const preview = previewRoute(result, sourceRect, targetRect)

    return {
      points: preview,
      segmentIndex:
        preview === result
          ? activeSegmentIndex
          : closestParallelSegment(preview, horizontal, intendedAxis, parallelPosition),
      merged,
    }
  }

  const canonical = previewRoute(result, sourceRect, targetRect)
  const canonicalSegmentIndex = closestParallelSegment(
    canonical,
    horizontal,
    intendedAxis,
    parallelPosition
  )

  return {
    points: canonical,
    segmentIndex: canonicalSegmentIndex,
    merged: canonical.length < result.length,
  }
}

/**
 * Insert a new waypoint at the midpoint of the segment `segmentIndex → segmentIndex + 1`.
 * The point is collinear with the segment, so the path stays orthogonal; the user
 * then drags it (moveVertexOrthogonal) to shape a new bend.
 *
 * Returns { points, index } with the inserted point's index, or null when the
 * segment index is out of range.
 */
export function insertWaypointOnSegment(
  points: EdgePoint[],
  segmentIndex: number
): { points: EdgePoint[]; index: number } | null {
  if (segmentIndex < 0 || segmentIndex >= points.length - 1) {
    return null
  }
  const from = points[segmentIndex]
  const to = points[segmentIndex + 1]
  const midpoint = { x: (from.x + to.x) / 2, y: (from.y + to.y) / 2 }
  const insertAt = segmentIndex + 1
  const result = [...points.slice(0, insertAt), midpoint, ...points.slice(insertAt)]

  return { points: result, index: insertAt }
}

/**
 * Drop interior waypoints that lie on the straight line between their neighbors
 * (redundant bends left behind by dragging a corner onto its neighbor's axis).
 * Endpoints are always kept.
 */
export function dedupeCollinear(points: EdgePoint[]): EdgePoint[] {
  return points.filter((point, index) => {
    if (index === 0 || index === points.length - 1) {
      return true
    }
    const prev = points[index - 1]
    const next = points[index + 1]
    const onHorizontalLine = isHorizontal(prev, point) && isHorizontal(point, next)
    const onVerticalLine = isVertical(prev, point) && isVertical(point, next)

    return !(onHorizontalLine || onVerticalLine)
  })
}

/**
 * Canonicalize an orthogonal route without changing its visible shape: remove
 * duplicate points and merge adjacent collinear segments. This is applied to
 * live previews as well as commits, so the displayed and persisted point lists
 * are identical.
 */
export function normalizeOrthogonalPoints(points: EdgePoint[]): EdgePoint[] {
  let normalized = points
    .filter(
      (point): point is EdgePoint => !!point && Number.isFinite(point.x) && Number.isFinite(point.y)
    )
    .filter(
      (point, index, finitePoints) =>
        index === 0 ||
        index === finitePoints.length - 1 ||
        !samePoint(point, finitePoints[index - 1])
    )
    .map((point) => ({ ...point }))

  while (
    normalized.length > 2 &&
    samePoint(normalized[normalized.length - 2], normalized[normalized.length - 1])
  ) {
    normalized.splice(normalized.length - 2, 1)
  }

  let previousLength = -1
  while (normalized.length > 2 && normalized.length !== previousLength) {
    previousLength = normalized.length
    normalized = dedupeCollinear(normalized)
  }

  return normalized
}

/** True when a persisted value can be used as an editable orthogonal route. */
export function isOrthogonalRoute(points: EdgePoint[] | null | undefined): points is EdgePoint[] {
  return (
    Array.isArray(points) &&
    points.length > 1 &&
    points.every(
      (point, index) =>
        !!point &&
        Number.isFinite(point.x) &&
        Number.isFinite(point.y) &&
        (index === 0 || isAxisAligned(points[index - 1], point))
    )
  )
}

/**
 * The stage of a sequence's lifecycle where a route failed the orthogonality
 * invariant. Used to tag invalid-route reports so each failure is traceable to
 * what produced the route (naming describes the failure domain, not the code
 * path that detected it). Values are stable telemetry keys for Sentry tags and
 * console logs.
 */
export enum SequenceRouteFailure {
  /** The router produced non-orthogonal points for a newly drawn connection. */
  InitialRouting = 'initial-routing',
  /**
   * A node created from the New Node panel could not be auto-connected to the
   * panel's connection source.
   */
  AutoConnect = 'auto-connect',
  /** Dragging a segment left the route non-orthogonal. */
  SegmentDrag = 'segment-drag',
  /** A batch of geometry updates contained one or more non-orthogonal routes. */
  BatchApply = 'batch-apply',
}

/**
 * Move one endpoint leg with its node while preserving the route from the next
 * structural bend onward. The endpoint receives the full node delta; its
 * adjacent waypoint follows only on the leg's shared axis. Thus a horizontal
 * endpoint leg follows vertical node movement and stretches for horizontal
 * movement (the vertical case is symmetric), without introducing a diagonal.
 *
 * A two-point route has no independent adjacent waypoint; centering below will
 * deterministically introduce the required L/Z corners after moving its endpoint.
 */
function moveEndpointLeg(
  points: EdgePoint[],
  endpoint: 'source' | 'target',
  delta: EdgePoint
): EdgePoint[] {
  if (points.length < 2) {
    return points
  }

  const result = points.map((point) => ({ ...point }))
  const endpointIndex = endpoint === 'source' ? 0 : result.length - 1
  const adjacentIndex = endpoint === 'source' ? 1 : result.length - 2
  const originalEndpoint = points[endpointIndex]
  const originalAdjacent = points[adjacentIndex]

  result[endpointIndex] = {
    x: originalEndpoint.x + delta.x,
    y: originalEndpoint.y + delta.y,
  }

  if (points.length > 2) {
    if (isHorizontal(originalEndpoint, originalAdjacent)) {
      result[adjacentIndex].y += delta.y
    } else if (isVertical(originalEndpoint, originalAdjacent)) {
      result[adjacentIndex].x += delta.x
    }
  }

  return result
}

function centeredDoglegForMovedStraightRoute(
  points: EdgePoint[],
  sourceDelta: EdgePoint,
  targetDelta: EdgePoint,
  sourceRect: EdgeRect,
  targetRect: EdgeRect
): EdgePoint[] | null {
  if (points.length !== 2 || !isAxisAligned(points[0], points[1])) {
    return null
  }

  const source = {
    x: points[0].x + sourceDelta.x,
    y: points[0].y + sourceDelta.y,
  }
  const target = {
    x: points[1].x + targetDelta.x,
    y: points[1].y + targetDelta.y,
  }
  const candidate = isHorizontal(points[0], points[1])
    ? normalizeOrthogonalPoints([
        source,
        { x: (source.x + target.x) / 2, y: source.y },
        { x: (source.x + target.x) / 2, y: target.y },
        target,
      ])
    : normalizeOrthogonalPoints([
        source,
        { x: source.x, y: (source.y + target.y) / 2 },
        { x: target.x, y: (source.y + target.y) / 2 },
        target,
      ])
  const sourceSide = sideAtBoundaryPoint(candidate[0], sourceRect)
  const targetSide = sideAtBoundaryPoint(candidate[candidate.length - 1], targetRect)

  // Preserve the original straight edge's attachment sides only while the
  // centered dogleg exits both nodes cleanly. If a node crosses its partner,
  // the general reconciliation below remains responsible for choosing safe
  // replacement sides and an outer detour.
  return sourceSide &&
    targetSide &&
    endpointSegmentsMatchSides(candidate, sourceSide, targetSide) &&
    !routeCrossesRectInterior(candidate, sourceRect) &&
    !routeCrossesRectInterior(candidate, targetRect)
    ? candidate
    : null
}

function collapseShortDoglegOnce(points: EdgePoint[]): EdgePoint[] {
  const last = points.length - 1
  for (let connectorIndex = 1; connectorIndex < last - 1; connectorIndex++) {
    const left = points[connectorIndex - 1]
    const connectorStart = points[connectorIndex]
    const connectorEnd = points[connectorIndex + 1]
    const right = points[connectorIndex + 2]
    const connectorLength =
      Math.abs(connectorEnd.x - connectorStart.x) + Math.abs(connectorEnd.y - connectorStart.y)
    const leftHorizontal = isHorizontal(left, connectorStart)
    const rightHorizontal = isHorizontal(connectorEnd, right)
    const leftIsSource = connectorIndex - 1 === 0
    const rightIsTarget = connectorIndex + 2 === last
    // A single segment cannot join differently aligned centered anchors. Keep
    // the tiny Z until the nodes line up exactly rather than moving an anchor.
    const anchorsPreventMerge = leftIsSource && rightIsTarget

    // Do not collapse a U-turn whose merged parallel span would disappear;
    // that would fold the route onto one endpoint leg and break its attachment.
    const mergedSpan = leftHorizontal ? Math.abs(right.x - left.x) : Math.abs(right.y - left.y)

    if (
      connectorLength <= NODE_DRAG_MERGE_THRESHOLD &&
      leftHorizontal === rightHorizontal &&
      !anchorsPreventMerge &&
      mergedSpan > MOVE_EPS
    ) {
      const axis = leftHorizontal
        ? leftIsSource
          ? left.y
          : rightIsTarget
            ? right.y
            : (left.y + right.y) / 2
        : leftIsSource
          ? left.x
          : rightIsTarget
            ? right.x
            : (left.x + right.x) / 2
      const mergedLeft = leftHorizontal ? { ...left, y: axis } : { ...left, x: axis }
      const mergedRight = leftHorizontal ? { ...right, y: axis } : { ...right, x: axis }
      const candidate = normalizeOrthogonalPoints([
        ...points.slice(0, connectorIndex - 1),
        mergedLeft,
        mergedRight,
        ...points.slice(connectorIndex + 3),
      ])
      const sourceOrientationPreserved =
        isHorizontal(points[0], points[1]) === isHorizontal(candidate[0], candidate[1])
      const candidateLast = candidate.length - 1
      const targetOrientationPreserved =
        isHorizontal(points[last - 1], points[last]) ===
        isHorizontal(candidate[candidateLast - 1], candidate[candidateLast])

      // A larger threshold can reach the short clearance stubs generated near
      // overlapping nodes. Never simplify those into a route that changes an
      // endpoint's perpendicular attachment.
      if (sourceOrientationPreserved && targetOrientationPreserved) {
        return candidate
      }
    }
  }

  return points
}

function overlappingSegmentSplice(
  points: EdgePoint[],
  firstIndex: number,
  secondIndex: number,
  horizontal: boolean
): EdgePoint[] | null {
  const firstStart = points[firstIndex]
  const firstEnd = points[firstIndex + 1]
  const secondStart = points[secondIndex]
  const secondEnd = points[secondIndex + 1]
  const sameAxis = horizontal
    ? Math.abs(firstStart.y - secondStart.y) <= MOVE_EPS
    : Math.abs(firstStart.x - secondStart.x) <= MOVE_EPS
  if (!sameAxis) {
    return null
  }

  const firstMin = Math.min(
    horizontal ? firstStart.x : firstStart.y,
    horizontal ? firstEnd.x : firstEnd.y
  )
  const firstMax = Math.max(
    horizontal ? firstStart.x : firstStart.y,
    horizontal ? firstEnd.x : firstEnd.y
  )
  const secondMin = Math.min(
    horizontal ? secondStart.x : secondStart.y,
    horizontal ? secondEnd.x : secondEnd.y
  )
  const secondMax = Math.max(
    horizontal ? secondStart.x : secondStart.y,
    horizontal ? secondEnd.x : secondEnd.y
  )
  const overlapStart = Math.max(firstMin, secondMin)
  const overlapEnd = Math.min(firstMax, secondMax)
  if (overlapEnd - overlapStart <= MOVE_EPS) {
    return null
  }

  // Splice where the later segment first enters the shared interval. Everything
  // between the two visits is a loop; the duplicated traversal disappears too.
  const secondStartAxis = horizontal ? secondStart.x : secondStart.y
  const spliceAxis = Math.min(Math.max(secondStartAxis, overlapStart), overlapEnd)
  const splice = horizontal
    ? { x: spliceAxis, y: firstStart.y }
    : { x: firstStart.x, y: spliceAxis }

  return normalizeOrthogonalPoints([
    ...points.slice(0, firstIndex + 1),
    splice,
    ...points.slice(secondIndex + 1),
  ])
}

function intersectingSegmentSplice(
  points: EdgePoint[],
  firstIndex: number,
  secondIndex: number,
  firstHorizontal: boolean
): EdgePoint[] | null {
  const firstStart = points[firstIndex]
  const firstEnd = points[firstIndex + 1]
  const secondStart = points[secondIndex]
  const secondEnd = points[secondIndex + 1]
  const horizontalStart = firstHorizontal ? firstStart : secondStart
  const horizontalEnd = firstHorizontal ? firstEnd : secondEnd
  const verticalStart = firstHorizontal ? secondStart : firstStart
  const verticalEnd = firstHorizontal ? secondEnd : firstEnd
  const intersection = { x: verticalStart.x, y: horizontalStart.y }

  if (
    !between(intersection.x, horizontalStart.x, horizontalEnd.x) ||
    !between(intersection.y, verticalStart.y, verticalEnd.y)
  ) {
    return null
  }

  // The route visits this point once on each segment. Keep the prefix before
  // the first visit and the suffix after the second; the path between those
  // visits is exactly the self-intersecting loop.
  return normalizeOrthogonalPoints([
    ...points.slice(0, firstIndex + 1),
    intersection,
    ...points.slice(secondIndex + 1),
  ])
}

function eraseSelfIntersectionLoopOnce(points: EdgePoint[]): EdgePoint[] {
  for (let firstIndex = 0; firstIndex < points.length - 1; firstIndex++) {
    const firstHorizontal = isHorizontal(points[firstIndex], points[firstIndex + 1])
    for (let secondIndex = firstIndex + 2; secondIndex < points.length - 1; secondIndex++) {
      const secondHorizontal = isHorizontal(points[secondIndex], points[secondIndex + 1])
      const spliced =
        firstHorizontal === secondHorizontal
          ? overlappingSegmentSplice(points, firstIndex, secondIndex, firstHorizontal)
          : intersectingSegmentSplice(points, firstIndex, secondIndex, firstHorizontal)
      if (spliced) {
        return spliced
      }
    }
  }

  return points
}

/**
 * Simplify topology exposed by a node drag without moving centered endpoints:
 * close short doglegs within the node-drag threshold, then erase loops bounded
 * by either overlapping or perpendicularly intersecting non-adjacent segments.
 * Repeating reaches a
 * deterministic fixed point for routes containing multiple nested folds.
 */
function simplifyRouteForNodeDrag(points: EdgePoint[]): EdgePoint[] {
  let result = normalizeOrthogonalPoints(points)
  const maxPasses = Math.max(points.length * 2, 1)

  for (let pass = 0; pass < maxPasses; pass++) {
    const collapsed = collapseShortDoglegOnce(result)
    if (collapsed !== result) {
      result = collapsed
    } else {
      const withoutLoop = eraseSelfIntersectionLoopOnce(result)
      if (withoutLoop === result) {
        return result
      }
      result = withoutLoop
    }
  }

  return result
}

/**
 * Deterministically reconnect and simplify a route after either endpoint node moves.
 *
 * - no endpoint movement: preserve persisted points byte-for-byte;
 * - both endpoint rects move by the same delta: translate the whole route;
 * - one endpoint moves: its endpoint leg follows the node, later structural
 *   bends stay fixed, any loop swallowed by the moved node is trimmed, tiny
 *   doglegs snap straight, and overlapping or intersecting route loops are erased.
 *
 * The same operation drives every node-drag frame and the drop commit.
 */
export function moveRouteForEndpointRects(
  points: EdgePoint[],
  originalSourceRect: EdgeRect,
  originalTargetRect: EdgeRect,
  sourceRect: EdgeRect,
  targetRect: EdgeRect
): EdgePoint[] {
  const sourceDelta = rectDelta(originalSourceRect, sourceRect)
  const targetDelta = rectDelta(originalTargetRect, targetRect)
  const sourceMoved = Math.abs(sourceDelta.x) > MOVE_EPS || Math.abs(sourceDelta.y) > MOVE_EPS
  const targetMoved = Math.abs(targetDelta.x) > MOVE_EPS || Math.abs(targetDelta.y) > MOVE_EPS

  if (!sourceMoved && !targetMoved) {
    return points
  }
  if (
    sourceMoved &&
    targetMoved &&
    Math.abs(sourceDelta.x - targetDelta.x) <= MOVE_EPS &&
    Math.abs(sourceDelta.y - targetDelta.y) <= MOVE_EPS
  ) {
    return points.map((point) => ({
      x: point.x + sourceDelta.x,
      y: point.y + sourceDelta.y,
    }))
  }

  // A direct route has no structural waypoint to hold fixed. When one endpoint
  // leaves the shared row/column, split it into a centered three-segment dogleg
  // instead of putting the new connector one clearance stub away from the node
  // that did not move.
  const centeredStraightRoute = centeredDoglegForMovedStraightRoute(
    points,
    sourceDelta,
    targetDelta,
    sourceRect,
    targetRect
  )
  if (centeredStraightRoute) {
    return centeredStraightRoute
  }

  let moved = points
  if (sourceMoved) {
    moved = moveEndpointLeg(moved, 'source', sourceDelta)
  }
  if (targetMoved) {
    moved = moveEndpointLeg(moved, 'target', targetDelta)
  }

  // Endpoint reconciliation and route simplification can expose another node
  // crossing or folded segment. Iterate both operations together so every live
  // preview frame already is the exact canonical route persisted on drop.
  let result = moved
  for (let pass = 0; pass < 6; pass++) {
    const next = simplifyRouteForNodeDrag(
      normalizeOrthogonalPoints(
        repairOrthogonalPoints(snapEndpointsToNodes(result, sourceRect, targetRect))
      )
    )
    if (sameRoute(result, next)) {
      return next
    }
    result = next
  }

  return result
}

/** SVG path for the exact point list used by preview and persistence. */
export function buildWaypointsPath(points: EdgePoint[]): string {
  return points.map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`).join(' ')
}

/**
 * Remove interior waypoint `index`, but only when the path stays orthogonal —
 * i.e. the neighbors it joined form an axis-aligned segment (a redundant or
 * user-added bend). Structural corners (whose removal would create a diagonal)
 * cannot be deleted, only moved: null is returned so the caller leaves the edge
 * untouched.
 */
export function removeWaypointOrthogonal(points: EdgePoint[], index: number): EdgePoint[] | null {
  const last = points.length - 1
  if (index <= 0 || index >= last) {
    return null
  }
  const prev = points[index - 1]
  const next = points[index + 1]
  if (!isAxisAligned(prev, next)) {
    return null
  }

  return dedupeCollinear([...points.slice(0, index), ...points.slice(index + 1)])
}
